// The frame both artifact↔record dialogs draw in: the Artifacts page's
// "Link to record" and the record page's "Attach artifact". They are the
// same act seen from either end, so they share one paper, one head, one
// section label and one footer, on the design system's tokens (the
// Documents Move dialog's treatment). Before this each carried its own
// copy of an older hex palette and the two had drifted from the rest of
// the app (Gerry, 2026-10-13).
import React, { useId } from "react";
import { Dialog } from "@mui/material";
import { Btn } from "@/components/FpPages/RMS/design/Button";

export const ArtifactDialogFrame: React.FC<{
  title: string;
  /** What the dialog acts on: the artifact, or the record. */
  subject: string;
  onClose: () => void;
  /** The footer's one button. Changes apply as they are made, so it only
   *  closes: "Done" where several changes can be made, "Cancel" where the
   *  first pick closes the dialog itself. */
  closeLabel: "Done" | "Cancel";
  children: React.ReactNode;
}> = ({ title, subject, onClose, closeLabel, children }) => {
  // The title names the dialog. MUI puts `aria-label` on the modal root,
  // not on the element with role="dialog", so the name goes by id.
  const titleId = useId();
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          // Phone gutters: the dialog takes the width and keeps a margin
          // rather than meeting the edges.
          sx: {
            margin: "16px",
            width: "calc(100% - 32px)",
            borderRadius: "12px",
            border: "1px solid var(--fp-border)",
            boxShadow: "var(--fp-lift)",
            backgroundImage: "none",
          },
        },
      }}
    >
      <div className="flex flex-col gap-4 p-4 md:p-5">
        <div className="min-w-0">
          <div
            id={titleId}
            className="text-[15px] font-semibold text-[var(--fp-ink)]"
          >
            {title}
          </div>
          <div className="truncate text-[12.5px] text-[var(--fp-ink-3)]">
            {subject}
          </div>
        </div>
        {children}
        <div className="flex items-center justify-end">
          <Btn onClick={onClose}>{closeLabel}</Btn>
        </div>
      </div>
    </Dialog>
  );
};

/** A section's caps label, as the records popover on the list draws it. */
export const ArtifactDialogSection: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div>
    <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[var(--fp-track-caps)] text-[var(--fp-ink-3)]">
      {label}
    </div>
    {children}
  </div>
);

export const ArtifactDialogError: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    role="alert"
    className="rounded-[8px] bg-[var(--fp-neg-tint)] px-3 py-2 text-[12.5px] text-[var(--fp-danger)]"
  >
    {children}
  </div>
);

/** A list well: hairline border, the same corners as the Move dialog's. */
export const ARTIFACT_DIALOG_WELL =
  "overflow-hidden rounded-[8px] border border-[var(--fp-border)]";
